import { Accordion, AccordionSummary, Box, AccordionDetails } from '@mui/material';
import { RankedLeaderboardsEntity } from 'app/interfaces/entity';
import { apiRequestCall, UrlParams } from 'app/services/api';
import { ApiResource } from 'app/services/api/enums';
import { useCancelToken } from 'app/services/api/hooks';
import { sessionSelector } from 'app/store/session/selectors';
import { sessionReducers } from 'app/store/session/slice';
import { AxiosRequestConfig } from 'axios';
import config from 'config/config';
import { LeaderboardsCarouselContent } from 'modules/leaderboards/components/LeaderboardsCarouselContent';
import { LeaderboardsCarouselHeader } from 'modules/leaderboards/components/LeaderboardsCarouselHeader';
import { CarouselProvider } from 'shared/providers';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useDispatch, useSelector } from 'react-redux';
import { Backdrop } from 'shared/components/Backdrop';
import { withDesktopView } from 'shared/HOC/withDesktopView';
import { useStyles } from './LeaderboardsCarousel.styles';

const LEADERBOARDS_LIMIT = 5;
const LEADERBOARD_REQUEST_PARAMS: AxiosRequestConfig & UrlParams = {
  method: 'GET',
  resource: ApiResource.Leaderboards,
  params: {
    limit: LEADERBOARDS_LIMIT,
    offset: 0,
    secondsAgo: 7 * 24 * 3600,
  },
};

const LeaderboardsCarouselComponent: FC = () => {
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const cancelToken = useCancelToken();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isLeaderboardExpandedSelector = useSelector(sessionSelector.isLeaderboardExpanded);
  const [isExpanded, setIsExpanded] = useState(isLeaderboardExpandedSelector);

  const [leaderboard, setLeaderboard] = useState<RankedLeaderboardsEntity | null>(null);

  const getLeaderboardsHandler = useCallback(() => {
    setIsLoading(true);
    apiRequestCall<RankedLeaderboardsEntity>({
      params: { ...LEADERBOARD_REQUEST_PARAMS, cancelToken },
      handlers: {
        success: (data) => {
          setIsLoading(false);
          setLeaderboard(data);
        },
        error: () => setIsLoading(false),
      },
      rejectOnCancel: false,
    });
  }, [cancelToken, setIsLoading]);

  const onChangeHandler = useCallback(
    (_, expanded) => {
      setIsExpanded(expanded);
      dispatch(sessionReducers.setIsLeaderboardExpanded(expanded));
    },
    [dispatch, setIsExpanded]
  );

  const slidesCount = useMemo(() => {
    if (!leaderboard) {
      return 0;
    }

    return Math.max(
      leaderboard.biggestWin?.length,
      leaderboard.mostWin?.length,
      leaderboard.mostGamesPlayed?.length
    );
  }, [leaderboard]);

  useEffect(() => {
    getLeaderboardsHandler();
  }, [getLeaderboardsHandler]);

  useEffect(() => {
    const interval = setInterval(
      () => getLeaderboardsHandler(),
      config.refreshLeaderboardInterval * 1000
    );

    return () => {
      clearInterval(interval);
    };
  }, [getLeaderboardsHandler]);

  if (!slidesCount) {
    return null;
  }

  return (
    <Box className={classes.root}>
      <CarouselProvider>
        <Accordion expanded={isExpanded ?? true} onChange={onChangeHandler}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box pr={1.5} width="100%">
              <LeaderboardsCarouselHeader isDotsPresent={isExpanded} slidesCount={slidesCount} />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              {isLoading && <Backdrop position="absolute" />}
              <LeaderboardsCarouselContent
                autoPlay={isExpanded}
                slidesCount={slidesCount}
                leaderboard={leaderboard}
              />
            </Box>
          </AccordionDetails>
        </Accordion>
      </CarouselProvider>
    </Box>
  );
};

export const LeaderboardsCarousel = withDesktopView(LeaderboardsCarouselComponent);
